import type { EquipmentSlot, ItemDef, ItemSetId, Rarity } from './types';

/**
 * Cyber-Vault Extraction item catalog (2026-05 HK-only cleanup).
 *
 * Structure:
 *  • 5 resources — for upgrade costs / decrypter fuel / boost costs
 *  • 8 Hollow-Knight set pieces (legendary, setId='hollow_knight') — sole
 *    equipment set, dropped piece-by-piece from regular vaults
 *  • 3 legendary weapons (sword/staff/lightning) — world-boss exclusive
 *  • 1 legendary amulet (death_prevention) — world-boss exclusive
 *
 * Total = 17 entries. The previous Vault-Knight set and the common
 * "starter" gear tier were removed: new players spawn with the full HK set
 * equipped (minus the weapon), and HK pieces are the only set-drops.
 */
export const ITEMS: Record<string, ItemDef> = {
  // ── Resources ───────────────────────────────────────────────
  scrap: {
    id: 'scrap', kind: 'junk', rarity: 'common', nameKey: 'scrap',
    baseValue: 2, stackable: true,
  },
  energy_cell: {
    id: 'energy_cell', kind: 'energy_cell', rarity: 'common', nameKey: 'energy_cell',
    baseValue: 5, stackable: true,
  },
  nano_chip: {
    id: 'nano_chip', kind: 'chip', rarity: 'uncommon', nameKey: 'nano_chip',
    baseValue: 25, stackable: true,
  },
  data_track: {
    id: 'data_track', kind: 'data_track', rarity: 'rare', nameKey: 'data_track',
    baseValue: 75, stackable: true,
  },
  hyper_processor_5: {
    id: 'hyper_processor_5', kind: 'hardware', rarity: 'epic', nameKey: 'hyper_processor_5',
    baseValue: 250, stackable: true,
  },

  // ── Starter Kit (common-tier, fresh-player only) ─────────────────────
  // Bare-bones equipment for brand-new characters so vault_alpha actually
  // feels like a starting challenge instead of trivial one-shot farming.
  // Players upgrade these out within the first 1-2 hours. The HK set is
  // the LATE-game goal, NOT the starter kit. Stats are intentionally low
  // (~10-20% of HK legendary): a fresh player should need 2-3 hits to kill
  // a swarmling and feel actual threat from boss-tier enemies.
  starter_blade: {
    id: 'starter_blade', kind: 'weapon', rarity: 'common', nameKey: 'starter_blade',
    baseValue: 20, stackable: false, equipSlot: 'weapon',
    stats: { attack: 4, defense: 0, health: 0 }, weaponClass: 'standard',
    mesh: { path: '/assets/3d/sets/hollow_knight/nail.glb', slot: 'hand_right', kind: 'skinned' },
  },
  starter_jacket: {
    id: 'starter_jacket', kind: 'armor', rarity: 'common', nameKey: 'starter_jacket',
    baseValue: 25, stackable: false, equipSlot: 'armor',
    stats: { attack: 0, defense: 3, health: 10 },
    mesh: { path: '/assets/3d/sets/hollow_knight/base.glb', slot: 'body' },
  },
  starter_helmet: {
    id: 'starter_helmet', kind: 'helmet', rarity: 'common', nameKey: 'starter_helmet',
    baseValue: 15, stackable: false, equipSlot: 'helmet',
    stats: { attack: 0, defense: 2, health: 5 },
    mesh: { path: '/assets/3d/sets/hollow_knight/mask_default.glb', slot: 'helmet', kind: 'skinned' },
  },

  // ── Hollow Knight Set (8 pieces, setId='hollow_knight') ──
  // Modular HK character authored in authoring/hollow_knight_character_v1.blend.
  // hk_armor swaps the entire body to hollow_knight/base.glb (Bip01-rigged HK
  // body); the other pieces are skinned attachments that re-bind to the HK
  // skeleton at equip time.
  //
  // P2.C rarity diversification: previously all 8 pieces were legendary, which
  // collided with the drop-table architecture (a single equip_set roll always
  // produced a legendary). Now the set spans rare→epic→legendary so the natural
  // drop pool produces graded loot. Set-bonus + aspectAffinity logic is
  // piece-count-based and unaffected by individual rarities.
  //   Legendary (2): hk_armor (body centerpiece), hk_nail (signature weapon)
  //   Epic (3):      hk_mask, hk_shield, hk_cloak
  //   Rare (3):      hk_heart, hk_drape, hk_scarf
  hk_armor: {
    id: 'hk_armor', kind: 'armor', rarity: 'legendary', nameKey: 'hk_armor',
    baseValue: 1500, stackable: false, equipSlot: 'armor',
    stats: { attack: 5, defense: 18, health: 80 },
    setId: 'hollow_knight',
    aspectAffinity: 'bulwark',
    mesh: { path: '/assets/3d/sets/hollow_knight/base.glb', slot: 'body' },
  },
  hk_mask: {
    id: 'hk_mask', kind: 'helmet', rarity: 'epic', nameKey: 'hk_mask',
    baseValue: 600, stackable: false, equipSlot: 'helmet',
    stats: { attack: 3, defense: 8, health: 30 },
    setId: 'hollow_knight',
    aspectAffinity: 'bulwark',
    mesh: { path: '/assets/3d/sets/hollow_knight/mask_default.glb', slot: 'helmet', kind: 'skinned' },
  },
  hk_cloak: {
    id: 'hk_cloak', kind: 'shoulder', rarity: 'epic', nameKey: 'hk_cloak',
    baseValue: 580, stackable: false, equipSlot: 'shoulder',
    stats: { attack: 3, defense: 7, health: 28 },
    setId: 'hollow_knight',
    aspectAffinity: 'bulwark',
    mesh: { path: '/assets/3d/sets/hollow_knight/cloak_tatter.glb', slot: 'shoulder', kind: 'skinned' },
  },
  hk_scarf: {
    id: 'hk_scarf', kind: 'belt', rarity: 'rare', nameKey: 'hk_scarf',
    baseValue: 430, stackable: false, equipSlot: 'belt',
    stats: { attack: 2, defense: 8, health: 32 },
    setId: 'hollow_knight',
    aspectAffinity: 'bulwark',
    mesh: { path: '/assets/3d/sets/hollow_knight/scarf_navy.glb', slot: 'belt', kind: 'skinned' },
  },
  hk_drape: {
    id: 'hk_drape', kind: 'sash', rarity: 'rare', nameKey: 'hk_drape',
    baseValue: 400, stackable: false, equipSlot: 'sash',
    stats: { attack: 4, defense: 3, health: 20 },
    setId: 'hollow_knight',
    aspectAffinity: 'bulwark',
    mesh: { path: '/assets/3d/sets/hollow_knight/drape_short.glb', slot: 'sash', kind: 'skinned' },
  },
  hk_heart: {
    id: 'hk_heart', kind: 'accessory', rarity: 'rare', nameKey: 'hk_heart',
    baseValue: 450, stackable: false, equipSlot: 'accessory',
    stats: { attack: 3, defense: 4, health: 40 },
    setId: 'hollow_knight',
    aspectAffinity: 'bulwark',
    mesh: { path: '/assets/3d/sets/hollow_knight/heart_void.glb', slot: 'torso', kind: 'skinned' },
  },
  hk_nail: {
    id: 'hk_nail', kind: 'weapon', rarity: 'legendary', nameKey: 'hk_nail',
    baseValue: 1300, stackable: false, equipSlot: 'weapon',
    stats: { attack: 32, defense: 0, health: 0 }, weaponClass: 'standard',
    setId: 'hollow_knight',
    aspectAffinity: 'bulwark',
    mesh: { path: '/assets/3d/sets/hollow_knight/nail.glb', slot: 'hand_right', kind: 'skinned' },
  },
  hk_shield: {
    id: 'hk_shield', kind: 'shield', rarity: 'epic', nameKey: 'hk_shield',
    baseValue: 650, stackable: false, equipSlot: 'shield',
    stats: { attack: 0, defense: 10, health: 30 },
    setId: 'hollow_knight',
    aspectAffinity: 'bulwark',
    mesh: { path: '/assets/3d/sets/hollow_knight/shield_soul.glb', slot: 'hand_left', kind: 'skinned' },
  },

  // ── Vault Knight Set (8 pieces, setId='vault_knight') ──
  // P3 second set — Stormcaller-aligned tank/striker with electric flavor.
  // Same rarity distribution as the diversified HK pool (2 legendary + 3
  // epic + 3 rare). Mesh paths are SHARED with HK placeholder geometry for
  // now (re-skin via SET_TINT='vault_knight' → cobalt blue). A future polish
  // pass will replace these paths with VK-dedicated GLBs.
  //   Legendary (2): vk_armor, vk_blade
  //   Epic (3):      vk_helm, vk_pauldrons, vk_buckler
  //   Rare (3):      vk_belt, vk_sash, vk_relic
  // Stats lean offensive (attack-heavy) vs HK's tanky balance, with bonus
  // affixes biased toward crit / special-damage via bonusStats rolls.
  vk_armor: {
    id: 'vk_armor', kind: 'armor', rarity: 'legendary', nameKey: 'vk_armor',
    baseValue: 1500, stackable: false, equipSlot: 'armor',
    stats: { attack: 8, defense: 15, health: 70 },
    setId: 'vault_knight',
    aspectAffinity: 'stormcaller',
    mesh: { path: '/assets/3d/sets/vault_knight/base.glb', slot: 'body' },
  },
  vk_helm: {
    id: 'vk_helm', kind: 'helmet', rarity: 'epic', nameKey: 'vk_helm',
    baseValue: 600, stackable: false, equipSlot: 'helmet',
    stats: { attack: 4, defense: 7, health: 28 },
    setId: 'vault_knight',
    aspectAffinity: 'stormcaller',
    mesh: { path: '/assets/3d/sets/vault_knight/mask_default.glb', slot: 'helmet', kind: 'skinned' },
  },
  vk_pauldrons: {
    id: 'vk_pauldrons', kind: 'shoulder', rarity: 'epic', nameKey: 'vk_pauldrons',
    baseValue: 580, stackable: false, equipSlot: 'shoulder',
    stats: { attack: 4, defense: 6, health: 26 },
    setId: 'vault_knight',
    aspectAffinity: 'stormcaller',
    mesh: { path: '/assets/3d/sets/vault_knight/cloak_tatter.glb', slot: 'shoulder', kind: 'skinned' },
  },
  vk_sash: {
    id: 'vk_sash', kind: 'sash', rarity: 'rare', nameKey: 'vk_sash',
    baseValue: 400, stackable: false, equipSlot: 'sash',
    stats: { attack: 5, defense: 3, health: 18 },
    setId: 'vault_knight',
    aspectAffinity: 'stormcaller',
    mesh: { path: '/assets/3d/sets/vault_knight/drape_short.glb', slot: 'sash', kind: 'skinned' },
  },
  vk_belt: {
    id: 'vk_belt', kind: 'belt', rarity: 'rare', nameKey: 'vk_belt',
    baseValue: 430, stackable: false, equipSlot: 'belt',
    stats: { attack: 3, defense: 6, health: 28 },
    setId: 'vault_knight',
    aspectAffinity: 'stormcaller',
    mesh: { path: '/assets/3d/sets/vault_knight/scarf_navy.glb', slot: 'belt', kind: 'skinned' },
  },
  vk_relic: {
    id: 'vk_relic', kind: 'accessory', rarity: 'rare', nameKey: 'vk_relic',
    baseValue: 450, stackable: false, equipSlot: 'accessory',
    stats: { attack: 4, defense: 3, health: 32 },
    setId: 'vault_knight',
    aspectAffinity: 'stormcaller',
    mesh: { path: '/assets/3d/sets/vault_knight/heart_void.glb', slot: 'torso', kind: 'skinned' },
  },
  vk_blade: {
    id: 'vk_blade', kind: 'weapon', rarity: 'legendary', nameKey: 'vk_blade',
    baseValue: 1300, stackable: false, equipSlot: 'weapon',
    stats: { attack: 36, defense: 0, health: 0 }, weaponClass: 'fast',
    setId: 'vault_knight',
    aspectAffinity: 'stormcaller',
    mesh: { path: '/assets/3d/sets/vault_knight/nail.glb', slot: 'hand_right', kind: 'skinned' },
  },
  vk_buckler: {
    id: 'vk_buckler', kind: 'shield', rarity: 'epic', nameKey: 'vk_buckler',
    baseValue: 650, stackable: false, equipSlot: 'shield',
    stats: { attack: 2, defense: 8, health: 28 },
    setId: 'vault_knight',
    aspectAffinity: 'stormcaller',
    mesh: { path: '/assets/3d/sets/vault_knight/shield_soul.glb', slot: 'hand_left', kind: 'skinned' },
  },

  // ── Legendary Weapons (3, world-boss exclusive) ─────────────────────
  // Each has a unique LegendaryEffect that defines a play-style.
  // Stats balanced around weaponClass DPS profile (heavy/standard/fast).
  legendary_sword: {
    id: 'legendary_sword', kind: 'weapon', rarity: 'legendary', nameKey: 'legendary_sword',
    baseValue: 1200, stackable: false, equipSlot: 'weapon',
    stats: { attack: 40, defense: 5, health: 20 }, weaponClass: 'heavy',
    legendaryEffect: 'armor_penetration',
    armorPenPct: 1.0,
    attackVisual: 'fire',
    aspectAffinity: 'vampire',
    mesh: {
      // KayKit weapons author the blade along weapon-local +Y. HK's R_Hand bone
      // points down-and-out at rest, so without compensation the blade aims at
      // the floor. Rotation [π/2, -0.611, 0] aligns the blade with world -Y
      // (forward) when the arm is hanging at the side.
      path: '/assets/3d/weapons/kaykit_sword_2handed_color.gltf', slot: 'hand_right', scale: 0.8,
      rotation: [0, 0.96, -Math.PI / 2],
      tintColor: '#ff5a1f', emissive: '#ff7028', emissiveIntensity: 0.9,
    },
  },
  legendary_staff: {
    id: 'legendary_staff', kind: 'weapon', rarity: 'legendary', nameKey: 'legendary_staff',
    baseValue: 1100, stackable: false, equipSlot: 'weapon',
    stats: { attack: 28, defense: 2, health: 10 }, weaponClass: 'standard',
    legendaryEffect: 'arcane_focus',
    aspectAffinity: 'pyromant',
    mesh: {
      path: '/assets/3d/weapons/kaykit_staff.gltf', slot: 'hand_right', scale: 0.8,
      rotation: [0, 0.96, -Math.PI / 2],
      // Icy palette. Emissive must stay << 1.0 per channel — MeshStandardMaterial
      // adds emissive*intensity ON TOP of map*color, so a near-white emissive at
      // intensity 1.5 (0.68·1.5=1.02 etc.) clamps the whole material to pure
      // white and the palette texture vanishes. Sword uses #ff7028·0.9 → (0.9,
      // 0.40, 0.14) which is safely below 1.0; staff now matches that profile.
      tintColor: '#7ec5ff', emissive: '#3e7da8', emissiveIntensity: 0.85,
    },
  },
  legendary_lightning: {
    id: 'legendary_lightning', kind: 'weapon', rarity: 'legendary', nameKey: 'legendary_lightning',
    baseValue: 1500, stackable: false, equipSlot: 'weapon',
    stats: { attack: 35, defense: 4, health: 0 }, weaponClass: 'fast',
    legendaryEffect: 'chain_lightning',
    attackVisual: 'lightning',
    aspectAffinity: 'stormcaller',
    // No mesh — LightningHandOrb is rendered procedurally via PlayerCharacter.lightningInHand.
  },

  // ── Legendary Accessory (1, world-boss exclusive) ───────────────────
  legendary_amulet: {
    id: 'legendary_amulet', kind: 'accessory', rarity: 'legendary', nameKey: 'legendary_amulet',
    baseValue: 950, stackable: false, equipSlot: 'accessory',
    stats: { attack: 2, defense: 8, health: 50 },
    legendaryEffect: 'death_prevention',
    aspectAffinity: 'vampire',
  },

  // ── P2.B Aspekt-Equipment (re-skinned HK pieces with non-bulwark affinity) ──
  // Re-uses HK GLBs with tinted emissive overrides — same skeleton, fits the
  // existing rig pipeline. Drops via the regular `equip_set`-pool roll (no
  // dedicated drop tag yet — refined in P3 when the 2nd set lands).
  pyromant_helmet: {
    id: 'pyromant_helmet', kind: 'helmet', rarity: 'legendary', nameKey: 'pyromant_helmet',
    baseValue: 950, stackable: false, equipSlot: 'helmet',
    stats: { attack: 6, defense: 7, health: 30 },
    aspectAffinity: 'pyromant',
    mesh: {
      path: '/assets/3d/sets/hollow_knight/mask_default.glb',
      slot: 'helmet', kind: 'skinned',
      tintColor: '#f97316', emissive: '#7a2e0a', emissiveIntensity: 0.9,
    },
  },
  stormcaller_cloak: {
    id: 'stormcaller_cloak', kind: 'shoulder', rarity: 'legendary', nameKey: 'stormcaller_cloak',
    baseValue: 920, stackable: false, equipSlot: 'shoulder',
    stats: { attack: 5, defense: 6, health: 28 },
    aspectAffinity: 'stormcaller',
    mesh: {
      path: '/assets/3d/sets/hollow_knight/cloak_tatter.glb',
      slot: 'shoulder', kind: 'skinned',
      tintColor: '#3b82f6', emissive: '#1c3a78', emissiveIntensity: 1.0,
    },
  },
  vampire_belt: {
    id: 'vampire_belt', kind: 'belt', rarity: 'legendary', nameKey: 'vampire_belt',
    baseValue: 850, stackable: false, equipSlot: 'belt',
    stats: { attack: 3, defense: 9, health: 40 },
    aspectAffinity: 'vampire',
    mesh: {
      path: '/assets/3d/sets/hollow_knight/scarf_navy.glb',
      slot: 'belt', kind: 'skinned',
      tintColor: '#dc2626', emissive: '#4a0e0e', emissiveIntensity: 0.9,
    },
  },
};

export function getItem(id: string): ItemDef | undefined {
  return ITEMS[id];
}

export function itemsByRarityAndSlot(rarity: Rarity, slot?: EquipmentSlot): ItemDef[] {
  // Excludes set items — they drop only via the dedicated 'equip_set' pool
  // (see dungeonStore DROP_TABLES).
  return Object.values(ITEMS).filter(
    (i) =>
      i.rarity === rarity
      && i.equipSlot !== undefined
      && i.setId === undefined
      && (!slot || i.equipSlot === slot),
  );
}

/** All items belonging to a given equipment set (drop pool for `equip_set`).
 *  Accepts any ItemSetId so callers can roll from a specific pool (hollow_knight,
 *  vault_knight). P3: multiple sets share the same `equip_set` drop slot —
 *  the picker function in dungeonStore decides which set's pool is rolled. */
export function itemsInSet(setId: ItemSetId): ItemDef[] {
  return Object.values(ITEMS).filter((i) => i.setId === setId);
}

export function itemsByRarity(rarity: Rarity): ItemDef[] {
  return Object.values(ITEMS).filter((i) => i.rarity === rarity && i.equipSlot !== undefined);
}
